import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  create_offering,
  delete_offering,
  fetch_dining_profile,
  fetch_offering_availability,
  fetch_offerings,
  fetch_public_offerings,
  save_dining_profile,
  update_offering,
} from './api';
import type { CreateOfferingInput, DiningProfileUpdate, UpdateOfferingInput } from './types';

const PROFILE = ['dining-profile'] as const;
const OFFERINGS = ['dining-offerings'] as const;

export function use_dining_profile() {
  return useQuery({ queryKey: PROFILE, queryFn: fetch_dining_profile });
}

export function use_save_dining_profile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DiningProfileUpdate) => save_dining_profile(input),
    onSuccess: (data) => qc.setQueryData(PROFILE, data),
  });
}

export function use_offerings() {
  return useQuery({ queryKey: OFFERINGS, queryFn: fetch_offerings });
}

function useInvalidatingOfferingMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: OFFERINGS }) });
}

export const use_create_offering = () => useInvalidatingOfferingMutation((i: CreateOfferingInput) => create_offering(i));
export const use_update_offering = () =>
  useInvalidatingOfferingMutation(({ id, input }: { id: string; input: UpdateOfferingInput }) => update_offering(id, input));
export const use_delete_offering = () => useInvalidatingOfferingMutation((id: string) => delete_offering(id));

// ---- Public (user console) ----
export function use_public_offerings(vendor_id?: string) {
  return useQuery({
    queryKey: ['public-offerings', vendor_id],
    queryFn: () => fetch_public_offerings(vendor_id as string),
    enabled: !!vendor_id,
  });
}

export function use_offering_availability(service_id: string | undefined, date: string, enabled = true) {
  return useQuery({
    queryKey: ['offering-availability', service_id, date],
    queryFn: () => fetch_offering_availability(service_id as string, date),
    enabled: !!service_id && enabled,
  });
}
